/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { Loader2, Trash2, MessageSquare, Tag } from 'lucide-react';
import { discussionsActions } from '../../actions/discussions.action';
import {
  Discussion,
  DiscussionComment,
  DiscussionCategory,
} from '../../types/discussions/discussions';

const DiscussionsManagement = () => {
  const [items, setItems] = useState<Discussion[]>([]);
  const [categories, setCategories] = useState<DiscussionCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // comments drawer
  const [openThread, setOpenThread] = useState<Discussion | null>(null);
  const [comments, setComments] = useState<DiscussionComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // category manager
  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState('');

  const { toast } = useToast();

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await discussionsActions.getDiscussions();
      setItems(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch discussions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await discussionsActions.getCategories();
      setCategories(res.data || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const openComments = async (d: Discussion) => {
    setOpenThread(d);
    try {
      setCommentsLoading(true);
      const res = await discussionsActions.getComments(d._id);
      setComments(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch comments', variant: 'destructive' });
    } finally {
      setCommentsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await discussionsActions.deleteDiscussion(deleteId);
      toast({ title: 'Success', description: 'Discussion removed' });
      setDeleteId(null);
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const removeComment = async (id: string) => {
    try {
      await discussionsActions.deleteComment(id);
      setComments((c) => c.filter((x) => x._id !== id));
      toast({ title: 'Success', description: 'Comment removed' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete comment', variant: 'destructive' });
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await discussionsActions.addCategory({ label: newCat.trim(), value: newCat.trim() });
      setNewCat('');
      fetchCategories();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add category', variant: 'destructive' });
    }
  };

  const removeCategory = async (id: string) => {
    try {
      await discussionsActions.deleteCategory(id);
      fetchCategories();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete category', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (catFilter !== 'all' && d.category !== catFilter) return false;
      if (!q) return true;
      return (
        d.title.toLowerCase().includes(q) ||
        d.authorName.toLowerCase().includes(q) ||
        d.message.toLowerCase().includes(q)
      );
    });
  }, [items, search, catFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Discussions</h1>
        <Button variant="outline" onClick={() => setShowCats(true)}>
          <Tag className="h-4 w-4 mr-2" />
          Manage Categories
        </Button>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Input
          placeholder="Search title, author, message"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:max-w-sm"
        />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="md:w-56">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c._id} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Threads ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Replies</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d._id}>
                  <TableCell className="font-medium">{d.title}</TableCell>
                  <TableCell>{d.category}</TableCell>
                  <TableCell>
                    {d.authorName}
                    {d.isGuest && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-200 text-amber-800">
                        guest
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{d.replies}</TableCell>
                  <TableCell>{d.views}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openComments(d)}>
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(d._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    No discussions.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Comments drawer */}
      <Dialog open={!!openThread} onOpenChange={() => setOpenThread(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{openThread?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{openThread?.message}</p>
          <hr className="my-2" />
          <p className="text-sm font-medium">Comments</p>
          {commentsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c._id} className="flex justify-between gap-3 rounded border p-3">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {c.authorName}
                      {c.isGuest && (
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-200 text-amber-800">
                          guest
                        </span>
                      )}
                      {c.createdAt && ` · ${new Date(c.createdAt).toLocaleString()}`}
                    </p>
                    <p className="text-sm">{c.message}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => removeComment(c._id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">No comments.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">Remove this discussion thread?</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
              No
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category manager */}
      <Dialog open={showCats} onOpenChange={setShowCats}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Discussion Categories</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              placeholder="New category"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
            />
            <Button onClick={addCategory} disabled={!newCat.trim()}>
              Add
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {categories.map((c) => (
              <div
                key={c._id}
                className="flex items-center justify-between rounded border px-3 py-2"
              >
                <span>{c.label}</span>
                <Button variant="outline" size="sm" onClick={() => removeCategory(c._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">No categories.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscussionsManagement;
